import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';
import { isRedeAdsConnectProfilePath } from '../constants/rede-ads-connect.constants';
import {
    ClientStorageKey,
    HubStorageKey,
    StorageService,
    TokenStorageKey,
} from '../services/storage/storage.service';
import { buildHubLoginUrlWithReturn, isHubRedirectActive } from './hub-redirect';
import { readHubTokenFromUrl } from './hub-product-token';

/** Access token do hub-app-front: claims hubUserId/userId, sem uid do login local do produto. */
export function isHubAccessToken(accessToken: string): boolean {
    try {
        const decoded = jwtDecode<Record<string, unknown>>(accessToken);
        if (decoded['uid'] != null && String(decoded['uid']).trim() !== '') {
            return false;
        }
        const hubUserId = decoded['hubUserId'];
        const userId = decoded['userId'];
        const hasHubUserId = hubUserId != null && String(hubUserId).trim() !== '';
        const hasUserId = userId != null && String(userId).trim() !== '';
        return hasHubUserId || hasUserId;
    } catch {
        return false;
    }
}

export function clearLocalProductSessionForEpochReset(storage: StorageService): void {
    storage.removeLocal(TokenStorageKey.ACCESS_TOKEN);
    storage.removeSession(TokenStorageKey.FROM_QUERY);
    storage.removeSession(ClientStorageKey.TELEGRAM_INFO);
    storage.removeSession(ClientStorageKey.TIMEZONE);
    storage.removeSession(ClientStorageKey.VTURB_API_KEY);
    storage.removeSession(ClientStorageKey.ACTIVE_ID);
    storage.removeSession(HubStorageKey.BOOTSTRAP_AT);
}

function buildHubReturnUrl(): string {
    const path = window.location.pathname || '/v2';
    const search = window.location.search || '';
    if (path.startsWith('/v2') || path.startsWith('/admin')) {
        return `${window.location.origin}${path}${search}`;
    }
    return `${window.location.origin}/v2`;
}

function markHubAuthEpoch(storage: StorageService, epoch: number): void {
    storage.setLocal(HubStorageKey.AUTH_EPOCH, String(epoch));
}

function readStoredHubAuthEpoch(storage: StorageService): number {
    const raw = storage.getLocal(HubStorageKey.AUTH_EPOCH);
    const parsed = Number.parseInt(raw ?? '0', 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/**
 * Na subida do app: invalida a sessão local quando `hubAuthEpoch` sobe
 * ou quando o accessToken não veio do Hub novo.
 * Quem chega com ?hubToken= não é afetado.
 * Desligado quando `hubAuthEpoch` é 0 ou o redirect para o Hub está inativo.
 */
export function enforceHubAuthEpochOnBoot(storage: StorageService): void {
    if (typeof window === 'undefined') {
        return;
    }

    const localAccessToken = (environment as { localAccessToken?: string }).localAccessToken?.trim() ?? '';
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (localAccessToken && isLocalHost) {
        storage.setLocal(TokenStorageKey.ACCESS_TOKEN, localAccessToken);
        return;
    }

    const targetEpoch = environment.hubAuthEpoch ?? 0;
    if (targetEpoch <= 0 || !isHubRedirectActive()) {
        return;
    }

    if (isRedeAdsConnectProfilePath(window.location.pathname)) {
        return;
    }

    if (readHubTokenFromUrl()) {
        markHubAuthEpoch(storage, targetEpoch);
        return;
    }

    const storedEpoch = readStoredHubAuthEpoch(storage);
    const accessToken = storage.getLocal(TokenStorageKey.ACCESS_TOKEN)?.trim() ?? '';
    const hadAccessToken = accessToken.length > 0;
    const epochStale = storedEpoch < targetEpoch;
    const tokenIsNotFromHub = hadAccessToken && !isHubAccessToken(accessToken);

    if (!epochStale && !tokenIsNotFromHub) {
        return;
    }

    clearLocalProductSessionForEpochReset(storage);
    markHubAuthEpoch(storage, targetEpoch);

    if (!hadAccessToken && !epochStale) {
        return;
    }

    window.location.href = buildHubLoginUrlWithReturn(buildHubReturnUrl());
}

export function createHubAuthEpochInitializer(storage: StorageService): () => Promise<void> {
    return () => {
        enforceHubAuthEpochOnBoot(storage);
        return Promise.resolve();
    };
}
